/**
 * Dev-only Options pill opening a tabbed MiniModal for debug toggles.
 */
import React, { useCallback, useRef, useState } from "react";
import MiniModal from "../MiniModal/MiniModal";
import MiniModalSection from "../MiniModal/MiniModalSection";
import MiniModalSwitch from "../MiniModal/MiniModalSwitch";
import MiniModalTabs, { MiniModalTab } from "../MiniModal/MiniModalTabs";
import MapDevDebugPill from "./MapDevDebugPill";

export interface OptionsDebugButtonProps {

    /** Enables dark palette variants when true. */
    "isDarkUi": boolean;

    /** Whether itinerary debug helpers are enabled in itinerary views. */
    "debugItineraries": boolean;

    /** Called when the debug itineraries switch changes. */
    "onDebugItinerariesChange": ( value: boolean ) => void;

    /** Whether stop preload bbox debug overlay is visible. */
    "showStopsBbox": boolean;

    /** Called when the Stops Bbox switch changes. */
    "onShowStopsBboxChange": ( value: boolean ) => void;

    /** Whether stop preload shrink debug overlay is visible. */
    "showStopsShrink": boolean;

    /** Called when the Stops Shrink switch changes. */
    "onShowStopsShrinkChange": ( value: boolean ) => void;

    /** Whether bus route shape debug layers are visible. */
    "showBusShapes": boolean;

    /** Called when the Bus shapes switch changes. */
    "onShowBusShapesChange": ( value: boolean ) => void;

    /** Whether the modern bike station popup is active. */
    "useModernBikeStationPopup": boolean;

    /** Called when the modern bike station popup switch changes. */
    "onUseModernBikeStationPopupChange": ( value: boolean ) => void;
}

/**
 * Renders the Options dev pill and its anchored MiniModal.
 *
 * @param props Current value and change handler for itinerary debug visibility.
 * @returns Options trigger pill and modal portal content.
 */
const OptionsDebugButton: React.FC<OptionsDebugButtonProps> = ( {
    isDarkUi,
    debugItineraries,
    onDebugItinerariesChange,
    showStopsBbox,
    onShowStopsBboxChange,
    showStopsShrink,
    onShowStopsShrinkChange,
    showBusShapes,
    onShowBusShapesChange,
    useModernBikeStationPopup,
    onUseModernBikeStationPopupChange,
} ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const [ activeTabId, setActiveTabId ] = useState( "options" );
    const triggerRef = useRef<HTMLButtonElement | null>( null );

    const handleOpen = useCallback( (): void => {
        setIsOpen( true );
    }, [] );

    const handleClose = useCallback( (): void => {
        setIsOpen( false );
    }, [] );

    return (
        <>
            <MapDevDebugPill
                ariaLabel="Open debug options"
                isDarkUi={isDarkUi}
                label="Options"
                onClick={handleOpen}
                ref={triggerRef}
                title="Itinerary debug options (dev only)"
            />
            <MiniModal
                anchorRef={triggerRef}
                isDarkUi={isDarkUi}
                isOpen={isOpen}
                maxWidth={320}
                onClose={handleClose}
                placement="left"
                title="Options"
            >
                <MiniModalTabs
                    activeTabId={activeTabId}
                    ariaLabel="Debug options sections"
                    isDarkUi={isDarkUi}
                    onActiveTabChange={setActiveTabId}
                >
                    <MiniModalTab
                        id="options"
                        label="Options"
                    >
                        <MiniModalSection isDarkUi={isDarkUi}>
                            <MiniModalSwitch
                                activeText="Debug itinéraires"
                                inactiveText="Debug itinéraires"
                                isDarkUi={isDarkUi}
                                onChange={onDebugItinerariesChange}
                                value={debugItineraries}
                            />
                        </MiniModalSection>
                        <MiniModalSection isDarkUi={isDarkUi}>
                            <MiniModalSwitch
                                activeText="Modern bike popup"
                                inactiveText="Modern bike popup"
                                isDarkUi={isDarkUi}
                                onChange={onUseModernBikeStationPopupChange}
                                value={useModernBikeStationPopup}
                            />
                        </MiniModalSection>
                    </MiniModalTab>
                    <MiniModalTab
                        id="layers"
                        label="Layers"
                    >
                        <MiniModalSection isDarkUi={isDarkUi}>
                            <MiniModalSwitch
                                activeText="Stops Bbox"
                                inactiveText="Stops Bbox"
                                isDarkUi={isDarkUi}
                                onChange={onShowStopsBboxChange}
                                value={showStopsBbox}
                            />
                        </MiniModalSection>
                        <MiniModalSection
                            isDarkUi={isDarkUi}
                        >
                            <MiniModalSwitch
                                activeText="Stops Shrink"
                                inactiveText="Stops Shrink"
                                isDarkUi={isDarkUi}
                                onChange={onShowStopsShrinkChange}
                                value={showStopsShrink}
                            />
                        </MiniModalSection>
                        <MiniModalSection
                            isDarkUi={isDarkUi}
                        >
                            <MiniModalSwitch
                                activeText="Bus shapes"
                                inactiveText="Bus shapes"
                                isDarkUi={isDarkUi}
                                onChange={onShowBusShapesChange}
                                value={showBusShapes}
                            />
                        </MiniModalSection>
                    </MiniModalTab>
                </MiniModalTabs>
            </MiniModal>
        </>
    );
};

export default OptionsDebugButton;
